"use strict";
import { V3_CORE_FACTORY_ADDRESSES } from "@uniswap/sdk-core";
import { Pool, computePoolAddress } from "@uniswap/v3-sdk";
import JSBI from "jsbi";
import { useMemo } from "react";
import { getChainInfo } from "uniswap/src/features/chains/chainInfo";
import { logger } from "utilities/src/logger/logger";
import { assume0xAddress } from "utils/wagmi";
import { useReadContracts } from "wagmi";
export class PoolCache {
  // Evict after 128 entries. Empirically, a swap uses 64 entries.
  static MAX_ENTRIES = 128;
  // These are FIFOs, using unshift/pop. This makes recent entries faster to find.
  static pools = [];
  static addresses = [];
  static getPoolAddress(factoryAddress, tokenA, tokenB, fee, chainId) {
    if (this.addresses.length > this.MAX_ENTRIES) {
      this.addresses = this.addresses.slice(0, this.MAX_ENTRIES / 2);
    }
    const { address: addressA } = tokenA;
    const { address: addressB } = tokenB;
    const key = `${factoryAddress}:${addressA}:${addressB}:${fee.toString()}`;
    const found = this.addresses.find((address2) => address2.key === key);
    if (found) {
      return found.address;
    }
    const address = {
      key,
      address: computePoolAddress({
        factoryAddress,
        tokenA,
        tokenB,
        fee,
        chainId: getChainInfo(chainId).sdkId
      })
    };
    this.addresses.unshift(address);
    return address.address;
  }
  static getPool(tokenA, tokenB, fee, sqrtPriceX96, liquidity, tick) {
    if (this.pools.length > this.MAX_ENTRIES) {
      this.pools = this.pools.slice(0, this.MAX_ENTRIES / 2);
    }
    const found = this.pools.find(
      (pool2) => pool2.token0 === tokenA && pool2.token1 === tokenB && pool2.fee === fee && JSBI.EQ(pool2.sqrtRatioX96, sqrtPriceX96) && JSBI.EQ(pool2.liquidity, liquidity) && pool2.tickCurrent === tick
    );
    if (found) {
      return found;
    }
    const pool = new Pool(tokenA, tokenB, fee, sqrtPriceX96, liquidity, tick);
    this.pools.unshift(pool);
    return pool;
  }
}
export var PoolState = /* @__PURE__ */ ((PoolState2) => {
  PoolState2[PoolState2["LOADING"] = 0] = "LOADING";
  PoolState2[PoolState2["NOT_EXISTS"] = 1] = "NOT_EXISTS";
  PoolState2[PoolState2["EXISTS"] = 2] = "EXISTS";
  PoolState2[PoolState2["INVALID"] = 3] = "INVALID";
  return PoolState2;
})(PoolState || {});
export function usePools(poolKeys, chainId) {
  const poolTokens = useMemo(() => {
    if (!chainId) {
      return new Array(poolKeys.length);
    }
    return poolKeys.map(([currencyA, currencyB, feeAmount]) => {
      if (currencyA && currencyB && feeAmount) {
        const tokenA = currencyA.wrapped;
        const tokenB = currencyB.wrapped;
        if (tokenA.equals(tokenB)) {
          return void 0;
        }
        return tokenA.sortsBefore(tokenB) ? [tokenA, tokenB, feeAmount] : [tokenB, tokenA, feeAmount];
      }
      return void 0;
    });
  }, [chainId, poolKeys]);
  const poolAddresses = useMemo(() => {
    const v3CoreFactoryAddress = chainId && V3_CORE_FACTORY_ADDRESSES[chainId];
    if (!v3CoreFactoryAddress) {
      return Array(poolTokens.length).fill(void 0);
    }
    return poolTokens.map((value) => value && PoolCache.getPoolAddress(v3CoreFactoryAddress, ...value, chainId));
  }, [chainId, poolTokens]);
  const { data: slot0s, isLoading: slot0sLoading } = useReadContracts({
    contracts: useMemo(() => {
      return poolAddresses.map(
        (address) => ({
          address: assume0xAddress(address) ?? "0x",
          // Edge case: if an address is undefined, we pass in a blank address to keep the result array the same length as poolAddresses
          abi: [
            {
              inputs: [],
              name: "slot0",
              outputs: [
                {
                  internalType: "uint160",
                  name: "sqrtPriceX96",
                  type: "uint160"
                },
                {
                  internalType: "int24",
                  name: "tick",
                  type: "int24"
                },
                {
                  internalType: "uint16",
                  name: "observationIndex",
                  type: "uint16"
                },
                {
                  internalType: "uint16",
                  name: "observationCardinality",
                  type: "uint16"
                },
                {
                  internalType: "uint16",
                  name: "observationCardinalityNext",
                  type: "uint16"
                },
                {
                  internalType: "uint8",
                  name: "feeProtocol",
                  type: "uint8"
                },
                {
                  internalType: "bool",
                  name: "unlocked",
                  type: "bool"
                }
              ],
              stateMutability: "view",
              type: "function"
            }
          ],
          functionName: "slot0",
          chainId
        })
      );
    }, [poolAddresses, chainId])
  });
  const { data: liquidities, isLoading: liquiditiesLoading } = useReadContracts({
    contracts: useMemo(() => {
      return poolAddresses.map(
        (address) => ({
          address: assume0xAddress(address) ?? "0x",
          // Edge case: if an address is undefined, we pass in a blank address to keep the result array the same length as poolAddresses
          abi: [
            {
              inputs: [],
              name: "liquidity",
              outputs: [
                {
                  internalType: "uint128",
                  name: "",
                  type: "uint128"
                }
              ],
              stateMutability: "view",
              type: "function"
            }
          ],
          functionName: "liquidity",
          chainId
        })
      );
    }, [poolAddresses, chainId])
  });
  return useComputePoolState(poolKeys, slot0s, slot0sLoading, liquidities, liquiditiesLoading, poolTokens);
}
export function useComputePoolState(poolKeys, slot0s, slot0sLoading, liquidities, liquiditiesLoading, poolTokens) {
  return useMemo(() => {
    return poolKeys.map((_key, index) => {
      if (slot0sLoading || liquiditiesLoading) {
        return [0 /* LOADING */, null];
      }
      const tokens = poolTokens[index];
      const slot0 = slot0s?.[index];
      const liquidity = liquidities?.[index];
      if (!tokens || !slot0 || !liquidity) {
        return [3 /* INVALID */, null];
      }
      if (!slot0.result) {
        return [1 /* NOT_EXISTS */, null];
      }
      if (typeof liquidity.result !== "bigint") {
        return [1 /* NOT_EXISTS */, null];
      }
      const [token0, token1, fee] = tokens;
      const [sqrtPriceX96, tick] = slot0.result;
      if (!sqrtPriceX96 || sqrtPriceX96 === 0n) {
        return [1 /* NOT_EXISTS */, null];
      }
      try {
        const pool = PoolCache.getPool(token0, token1, fee, sqrtPriceX96.toString(), liquidity.result.toString(), tick);
        return [2 /* EXISTS */, pool];
      } catch (error) {
        logger.error(error, {
          tags: {
            file: "usePools",
            function: "usePools"
          },
          extra: {
            token0: token0.address,
            token1: token1.address,
            chainId: token0.chainId,
            fee
          }
        });
        return [1 /* NOT_EXISTS */, null];
      }
    });
  }, [poolKeys, slot0sLoading, liquiditiesLoading, poolTokens, slot0s, liquidities]);
}
export function usePool(currencyA, currencyB, feeAmount) {
  const poolKeys = useMemo(
    () => [[currencyA, currencyB, feeAmount]],
    [currencyA, currencyB, feeAmount]
  );
  return usePools(poolKeys, currencyA?.chainId)[0];
}
